import { Injectable } from '@angular/core';
import { MaintenanceAndTasksLogsMapped, MaintenanceLog } from '@core/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { constantCase } from 'change-case';
import { clone, isEqual } from 'lodash';
import moment from 'moment-timezone';

const clientApplicationNameMap = {
  gorgoroth: 'mobile',
  mountdoom: 'browser',
  system: 'system',
  api: 'api'
};

@Injectable({
  providedIn: 'root'
})
export class CreateMaintenanceLogsService {

  constructor(private translateService: TranslateService) {}

  public listMaintenancesLogsMapped(maintenanceLogs: MaintenanceLog[]): MaintenanceAndTasksLogsMapped[] {
    const logsMapped: MaintenanceAndTasksLogsMapped[] = [];

    maintenanceLogs.forEach((currentMaintenanceLog, index) => {
      const previousMaintenanceLog = maintenanceLogs[index - 1];
      const maintenanceLog = {
        ...clone(currentMaintenanceLog),
        icon: 'icon-plus-circle-outline',
        typeLog: 'maintenance',
        colorBackground: 'new--background',
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.CREATED'),
        dateCreatedAt: moment(currentMaintenanceLog.createdAt).local().format('DD/MM/YYYY'),
        dateTimeCreatedAt: moment(currentMaintenanceLog.createdAt).local().format('DD/MM/YYYY HH:mm:ss'),
        clientApplicationName: clientApplicationNameMap[currentMaintenanceLog.clientApplicationName] || currentMaintenanceLog.clientApplicationName,
        showMessage: false
      };

      const createdMaintenanceLog = this.generateCreateLog(maintenanceLog, previousMaintenanceLog);
      if (createdMaintenanceLog) {
        logsMapped.push(createdMaintenanceLog);
      }

      if (createdMaintenanceLog) {
        return;
      }

      const scheduleSuggestionLog = this.generateScheduleSuggestionLog(maintenanceLog, previousMaintenanceLog);
      if (scheduleSuggestionLog) {
        logsMapped.push(scheduleSuggestionLog);
      }

      const updatedSegmentLog = this.generateUpdatedSegmentLog(maintenanceLog, previousMaintenanceLog);
      if (updatedSegmentLog) {
        logsMapped.push(updatedSegmentLog);
      }

      const updatedTypeLog = this.generateUpdatedMaintenanceTypeLog(maintenanceLog, previousMaintenanceLog);
      if (updatedTypeLog) {
        logsMapped.push(updatedTypeLog);
      }

      const updatedMessageLog = this.generateUpdatedMessageLog(maintenanceLog, previousMaintenanceLog);
      if (updatedMessageLog) {
        logsMapped.push(updatedMessageLog);
      }

      const assignedUserLog = this.generateAssignedToUserLog(maintenanceLog, previousMaintenanceLog);
      if (assignedUserLog) {
        logsMapped.push(assignedUserLog);
      }

      const unassignedUserLog = this.generateUnassignedUserLog(maintenanceLog, previousMaintenanceLog);
      if (unassignedUserLog) {
        logsMapped.push(unassignedUserLog);
      }

      const updatedAssigneeLog = this.generateUpdateAssignedLog(maintenanceLog, previousMaintenanceLog);
      if (updatedAssigneeLog) {
        logsMapped.push(updatedAssigneeLog);
      }

      const companyAssignedLog = this.generateAssigneeCompanyLog(maintenanceLog, previousMaintenanceLog);
      if (companyAssignedLog) {
        logsMapped.push(companyAssignedLog);
      }

      const reallocatedCompanyLog = this.generateRelocatedLog(maintenanceLog, previousMaintenanceLog);
      if (reallocatedCompanyLog) {
        logsMapped.push(reallocatedCompanyLog);
      }

      const canceledMaintenanceLog = this.generateCanceledLog(maintenanceLog, previousMaintenanceLog);
      if (canceledMaintenanceLog) {
        logsMapped.push(canceledMaintenanceLog);
      }

      const refusedMaintenanceLog = this.generateRefusedLog(maintenanceLog, previousMaintenanceLog);
      if (refusedMaintenanceLog) {
        logsMapped.push(refusedMaintenanceLog);
      }

      const reopenMaintenanceLog = this.generateReopenedLog(maintenanceLog, previousMaintenanceLog);
      if (reopenMaintenanceLog) {
        logsMapped.push(reopenMaintenanceLog);
      }

      const archivedMaintenanceLog = this.generateArchivedLog(maintenanceLog, previousMaintenanceLog);
      if (archivedMaintenanceLog) {
        logsMapped.push(archivedMaintenanceLog);
      }

      const unarchivedMaintenanceLog = this.generateUnarchivedLog(maintenanceLog, previousMaintenanceLog);
      if (unarchivedMaintenanceLog) {
        logsMapped.push(unarchivedMaintenanceLog);
      }
    });
    return logsMapped;
  }

  private generateCreateLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    if (previousMaintenanceLog) {
      return null;
    }

    let createdLogBy = null;
    if (String(log.clientApplicationName) === 'system') {
      createdLogBy = 'recurrence';
    }

    if (log.updatedBy && log.updatedBy.type && log.updatedBy.type.includes('eyeofsauron')) {
      createdLogBy = 'eyeofsauron';
    }

    return {
      ...clone(log),
      createdLogBy,
      showMessage: true,
      isCreatedLog: true,
    };
  }

  private generateCanceledLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    const isUpdatedByEyeofsaruon = log.updatedBy && log.updatedBy.type && log.updatedBy.type.includes('eyeofsauron');

    const isChangedFromRequestedToCanceled = previousMaintenanceLog.status === 'requested' && log.status === 'canceled';

    const isRefused = isUpdatedByEyeofsaruon && isChangedFromRequestedToCanceled;

    const isChangedToCanceled =  previousMaintenanceLog && previousMaintenanceLog.status !== 'canceled' && log.status === 'canceled';

    if (isChangedToCanceled && !isRefused ) {
      return {
        ...clone(log),
        icon: 'icon-close',
        colorBackground: 'canceled--background',
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.CANCELED'),
        eventDescription: this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.NOTE.REASON', {
          reason: log.statusDescription || this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.NOTE.NOT_COMPLETED')
        }),
      };
    }
    return null;
  }

  private generateRefusedLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    const isUpdatedByEyeofsaruon = log.updatedBy && log.updatedBy.type && log.updatedBy.type.includes('eyeofsauron');

    const isChangedFromRequestedToCanceled = previousMaintenanceLog.status === 'requested' && log.status === 'canceled';

    const isRefused = isUpdatedByEyeofsaruon && isChangedFromRequestedToCanceled;

    if (isRefused) {
      return {
        ...clone(log),
        icon: 'icon-close',
        colorBackground: 'refused--background',
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.REFUSED'),
        eventDescription: this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.NOTE.REASON', {
          reason: log.statusDescription || this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.NOTE.NOT_COMPLETED')
        }),
      };
    }
    return null;
  }


  private generateAssigneeCompanyLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    if (!previousMaintenanceLog.company && log.company) {
      return {
        ...clone(log),
        image: 'ic_location_marker_19px',
        colorBackground: 'assigned-to-company--background',
        createdAt: moment(log.createdAt).add(1, 'second').toISOString(),
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.ASSIGNED_TO_COMPANY'),
        eventDescription: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.DESCRIPTION.WAS_ASSIGNED_COMPANY', {
          company: log.company.name
        })
      };
    }
    return null;
  }

  private generateUpdatedMessageLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    if (log.message !== previousMaintenanceLog.message) {
      return {
        ...clone(log),
        image: 'ic_chat_20px',
        colorBackground: 'description-updated--background',
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.DESCRIPTION_UPDATED'),
        showMessage: true
      };
    }
    return null;
  }

  private generateUpdatedSegmentLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    if ((log.segment && log.segment.id) !== (previousMaintenanceLog && previousMaintenanceLog.segment && previousMaintenanceLog.segment.id)) {
      return {
        ...clone(log),
        image: 'new_icon_edit_action-20px',
        colorBackground: 'segment-updated--background',
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.SEGMENT_UPDATED', { newSegmentName: log.segment.name }),
        showMessage: false
      };
    }
    return null;
  }


  private generateUpdatedMaintenanceTypeLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    if ((log && log.maintenanceType && log.maintenanceType.id) !== (previousMaintenanceLog && previousMaintenanceLog.maintenanceType && previousMaintenanceLog.maintenanceType.id)) {
      return {
        ...clone(log),
        image: 'new_icon_edit_action-20px',
        colorBackground: 'maintenance-type-updated--background',
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.MAINTENANCE_TYPE_UPDATED', { newMaintenanceTypeName: log.maintenanceType.name }),
        showMessage: false
      };
    }
    return null;
  }

  private generateAssignedToUserLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    if (!previousMaintenanceLog.assignee && log.assignee) {
      return {
        ...clone(log),
        image: 'ic_user_18px',
        colorBackground: 'assigned-to-user--background',
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.ASSIGNED'),
        eventDescription: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.DESCRIPTION.ASSIGNED', {
          name: log.assignee && log.assignee.name
        })
      };
    }
    return null;
  }

  private generateUnassignedUserLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    if (previousMaintenanceLog.assignee && !log.assignee) {
      return {
        ...clone(log),
        image: 'ic_user_add_18px',
        colorBackground: 'assigned-to-user--background',
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.CHANGE_ASSIGNEE'),
        eventDescription: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.DESCRIPTION.UNASSIGNED', {
          name: previousMaintenanceLog.assignee.name
        })
      };
    }
    return null;
  }

  private generateUpdateAssignedLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    if (
      previousMaintenanceLog.assignee && log.assignee &&
      previousMaintenanceLog.assignee.id !== log.assignee.id
    ) {
      return {
        ...clone(log),
        image: 'ic_user_add_18px',
        colorBackground: 'assigned-to-user--background',
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.CHANGE_ASSIGNEE'),
        eventDescription: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.DESCRIPTION.ASSIGNED_UPDATED', {
          previousAssignee: previousMaintenanceLog.assignee.name,
          currentAssignee: log.assignee.name
        })
      };


    }
    return null;
  }

  private generateRelocatedLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    if (
      previousMaintenanceLog.company && log.company &&
      previousMaintenanceLog.company.id !== log.company.id
    ) {
      return {
        ...clone(log),
        icon: 'icon-swap-vertical',
        colorBackground: 'relocated--background',
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.RELOCATED'),
        eventDescription: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.DESCRIPTION.RELOCATED', {
          previousCompany: previousMaintenanceLog.company.name,
          currentCompany: log.company.name
        }),
        eventNote: log.relocationReason && this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.NOTE.REASON', {
          reason: log.relocationReason
        })
      };
    }
    return null;
  }

  private generateReopenedLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    if (previousMaintenanceLog.status === 'canceled' && log.status === 'new' && log.statusDescription) {
      return {
        ...clone(log),
        icon: 'icon-replay',
        colorBackground: 'reopened--background',
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.REOPENED'),
        eventDescription: this.translateService.instant('MAINTENANCES.TABS.LOGS.TASK.NOTE.REASON', {
          reason: log.statusDescription
        })
      };
    }
    return null;
  }

  private generateArchivedLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    if (previousMaintenanceLog && !previousMaintenanceLog.archived && log.archived) {
      return {
        ...clone(log),
        icon: 'icon-box-download',
        colorBackground: 'archive--background',
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.ARCHIVED'),
      };
    }
    return null;
  }

  private generateUnarchivedLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    if (previousMaintenanceLog && previousMaintenanceLog.archived && !log.archived) {
      return {
        ...clone(log),
        icon: 'icon-box-upload',
        colorBackground: 'unarchive--background',
        eventName: this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.UNARCHIVED'),
      };
    }
    return null;
  }

  private generateScheduleSuggestionLog(log: MaintenanceAndTasksLogsMapped, previousMaintenanceLog: MaintenanceLog): MaintenanceAndTasksLogsMapped {
    const isFistLogWithScheduleSuggestion = !previousMaintenanceLog && log.scheduleSuggestions && log.scheduleSuggestions.length > 0;
    const isScheduleSuggestionStatusChanged = previousMaintenanceLog && log.scheduleSuggestions && !isEqual(log.scheduleSuggestionsStatus, previousMaintenanceLog.scheduleSuggestionsStatus);
    const isScheduleSuggestionChanged = previousMaintenanceLog && log.scheduleSuggestions && !isEqual(log.scheduleSuggestions, previousMaintenanceLog.scheduleSuggestions);


    if (isFistLogWithScheduleSuggestion || isScheduleSuggestionStatusChanged || isScheduleSuggestionChanged) {

      const datesFormatted = log.scheduleSuggestions.map((suggestion) => {
        const dateFormatted = moment(suggestion.date).format('DD/MM/YYYY');
        if (!suggestion.time) { return dateFormatted; }
        const [hour, minute] = suggestion.time.split(':');
        return `${dateFormatted} ${hour}:${minute}`;
      });

      const eventNoteLines = datesFormatted.map((dateFormatted, index) => {
        return this.translateService.instant('MAINTENANCES.TABS.LOGS.MAINTENANCE.SCHEDULE_SUGGESTION_NOTE', { number: index + 1, dateTime: dateFormatted });
      });

      const eventNote = eventNoteLines.join('<br>');

      const status = log.scheduleSuggestionsStatus === 'corporateSuggested' && (log.scheduleSuggestions == null || log.scheduleSuggestions.length === 0) ? 'removed' : log.scheduleSuggestionsStatus;

      return {
        ...clone(log),
        isCreatedLog: isFistLogWithScheduleSuggestion,
        icon: 'icon-calendar-clock',
        colorBackground: 'schedule-suggestion--background',
        eventName: this.translateService.instant(`MAINTENANCES.TABS.LOGS.MAINTENANCE.SCHEDULE_SUGGESTION`, {
          status: this.translateService.instant(`MAINTENANCES.TABS.LOGS.MAINTENANCE.SCHEDULE_SUGGESTION_STATUS_${constantCase(status)}`)
        }),
        eventNote: eventNote
      };
    }
  }
}
